import React, { useEffect, useMemo, useState } from 'react';
import { Search, BookOpen, Clock, Calendar, ArrowRight, X, Filter } from 'lucide-react';
import { AnimatedGridPattern } from '../components/ui/AnimatedGridPattern';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { fetchBlogArticles, getCategories, formatDate, BlogArticle } from '../lib/contentful';
interface BlogPageProps {
  onNavigate: (page: string, data?: any) => void;
}
export function BlogPage({
  onNavigate
}: BlogPageProps) {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  useEffect(() => {
    async function loadArticles() {
      try {
        setLoading(true);
        const data = await fetchBlogArticles();
        setArticles(data);
      } catch (err) {
        setError('Artikel konnten nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    }
    loadArticles();
  }, []);
  const categories = useMemo(() => getCategories(articles), [articles]);
  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      const matchesSearch = !searchQuery || article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !selectedCategory || article.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [articles, searchQuery, selectedCategory]);
  const featuredArticle = filteredArticles[0];
  const remainingArticles = filteredArticles.slice(1);
  return <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300" data-id="element-544">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-sky-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-blue-950/20 py-16 lg:py-24 border-b border-slate-200 dark:border-slate-800" data-id="element-545">
        <AnimatedGridPattern numSquares={30} maxOpacity={0.15} duration={3} repeatDelay={1} className="[mask-image:radial-gradient(600px_circle_at_center,white,transparent)] inset-x-0 inset-y-[-30%] h-[200%] skew-y-12" data-id="element-546" />

        <div className="container mx-auto px-4 text-center relative z-10" data-id="element-547">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 dark:bg-blue-950/30 px-3 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-900 mb-6" data-id="element-548">
            <BookOpen className="h-3.5 w-3.5" data-id="element-549" />
            <span data-id="element-550">Wissen & Ratgeber</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6 tracking-tight" data-id="element-551">
            Versicherungs-Ratgeber
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10" data-id="element-552">
            Tipps, Vergleiche und Grundlagen – verständlich erklärt. Damit du
            informierte Entscheidungen treffen kannst.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative" data-id="element-553">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-554">
              <Search className="h-5 w-5 text-slate-400" data-id="element-555" />
            </div>
            <input type="text" placeholder="Artikel suchen..." className="w-full pl-12 pr-12 py-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-base" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-556" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors" data-id="element-557">
                <X className="h-5 w-5" data-id="element-558" />
              </button>}
          </div>

          {/* Category Filters */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2" data-id="element-559">
            <button onClick={() => setSelectedCategory(null)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!selectedCategory ? 'bg-blue-600 text-white shadow-md' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'}`} data-id="element-560">
              Alle
            </button>
            {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === category ? 'bg-blue-600 text-white shadow-md' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'}`} data-id="element-561">
                {category}
              </button>)}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 lg:py-16 max-w-6xl" data-id="element-562">
        {/* Loading State */}
        {loading && <div className="flex flex-col items-center justify-center py-20" data-id="element-563">
            <div className="h-12 w-12 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin mb-4" data-id="element-564"></div>
            <p className="text-slate-500 dark:text-slate-400" data-id="element-565">
              Artikel werden geladen...
            </p>
          </div>}

        {/* Error State */}
        {error && !loading && <div className="text-center py-20" data-id="element-566">
            <div className="inline-flex h-16 w-16 bg-red-100 dark:bg-red-900/30 rounded-2xl items-center justify-center mb-6" data-id="element-567">
              <X className="h-8 w-8 text-red-500" data-id="element-568" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2" data-id="element-569">
              Fehler beim Laden
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6" data-id="element-570">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline" data-id="element-571">
              Erneut versuchen
            </Button>
          </div>}

        {/* No Results */}
        {!loading && !error && filteredArticles.length === 0 && <div className="text-center py-20" data-id="element-572">
            <div className="inline-flex h-16 w-16 bg-slate-100 dark:bg-slate-800 rounded-2xl items-center justify-center mb-6" data-id="element-573">
              <Search className="h-8 w-8 text-slate-400" data-id="element-574" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2" data-id="element-575">
              Keine Artikel gefunden
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6" data-id="element-576">
              Versuche einen anderen Suchbegriff oder{' '}
              <button onClick={() => {
            setSearchQuery('');
            setSelectedCategory(null);
          }} className="text-blue-600 dark:text-blue-400 hover:underline" data-id="element-577">
                zeige alle Artikel
              </button>
            </p>
          </div>}

        {/* Articles */}
        {!loading && !error && filteredArticles.length > 0 && <div className="space-y-12" data-id="element-578">
            {/* Featured Article */}
            {featuredArticle && <div className="group relative bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-800 hover:shadow-xl transition-all duration-300 cursor-pointer" onClick={() => onNavigate('blog-article', {
          slug: featuredArticle.slug
        })} data-id="element-579">
                <div className="grid md:grid-cols-2 gap-0" data-id="element-580">
                  {/* Image Placeholder */}
                  <div className="relative h-64 md:h-auto bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 flex items-center justify-center" data-id="element-581">
                    <BookOpen className="h-20 w-20 text-blue-300 dark:text-blue-700" data-id="element-582" />
                    <div className="absolute top-4 left-4" data-id="element-583">
                      <Badge className="bg-blue-600 text-white border-none" data-id="element-584">
                        {featuredArticle.category}
                      </Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 md:p-8 flex flex-col justify-center" data-id="element-585">
                    <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400 mb-3" data-id="element-586">
                      <span className="flex items-center gap-1.5" data-id="element-587">
                        <Clock className="h-4 w-4" data-id="element-588" />
                        {featuredArticle.readTime}
                      </span>
                      <span className="flex items-center gap-1.5" data-id="element-589">
                        <Calendar className="h-4 w-4" data-id="element-590" />
                        {formatDate(featuredArticle.publishedDate)}
                      </span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-591">
                      {featuredArticle.title}
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed" data-id="element-592">
                      {featuredArticle.excerpt}
                    </p>
                    <div data-id="element-593">
                      <span className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium group-hover:gap-3 transition-all" data-id="element-594">
                        Artikel lesen
                        <ArrowRight className="h-4 w-4" data-id="element-595" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>}

            {/* Article Grid */}
            {remainingArticles.length > 0 && <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-596">
                {remainingArticles.map(article => <div key={article.id} className="group bg-white dark:bg-slate-900 rounded-xl overflow-hidden shadow-sm border border-slate-200 dark:border-slate-800 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer" onClick={() => onNavigate('blog-article', {
            slug: article.slug
          })} data-id="element-597">
                    {/* Image Placeholder */}
                    <div className="relative h-40 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center" data-id="element-598">
                      <BookOpen className="h-12 w-12 text-slate-300 dark:text-slate-600" data-id="element-599" />
                      <div className="absolute top-3 left-3" data-id="element-600">
                        <Badge variant="secondary" className="text-xs" data-id="element-601">
                          {article.category}
                        </Badge>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5" data-id="element-602">
                      <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mb-2" data-id="element-603">
                        <span className="flex items-center gap-1" data-id="element-604">
                          <Clock className="h-3.5 w-3.5" data-id="element-605" />
                          {article.readTime}
                        </span>
                        <span data-id="element-606">{formatDate(article.publishedDate)}</span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2" data-id="element-607">
                        {article.title}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-4" data-id="element-608">
                        {article.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 font-medium" data-id="element-609">
                        Weiterlesen
                        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" data-id="element-610" />
                      </span>
                    </div>
                  </div>)}
              </div>}
          </div>}

        {/* CTA Section */}
        <div className="mt-16 relative bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 md:p-10 text-white shadow-xl overflow-hidden text-center" data-id="element-611">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32" data-id="element-612"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl -ml-32 -mb-32" data-id="element-613"></div>

          <div className="relative z-10 max-w-xl mx-auto" data-id="element-614">
            <h3 className="text-2xl font-bold mb-3" data-id="element-615">
              Persönliche Beratung gewünscht?
            </h3>
            <p className="text-blue-100 mb-6" data-id="element-616">
              Unsere Experten helfen dir, die richtige Versicherung zu finden –
              individuell und unabhängig.
            </p>
            <Button variant="white" onClick={() => onNavigate('beratung')} className="hover:scale-105 transition-transform" data-id="element-617">
              Beratung anfragen
              <ArrowRight className="ml-2 h-4 w-4" data-id="element-618" />
            </Button>
          </div>
        </div>
      </div>
    </div>;
}